import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import { CircleX } from 'lucide-react'
import { v4 as uuid } from 'uuid'
import { format, isBefore } from 'date-fns'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import Spinner from '../../components/Spinner'
import { api } from '../../services/api'
import Card from '../../components/Card'
import { Button, Textbox } from '../../components/Form'
import { FiAlertTriangle, FiX, FiFileText } from 'react-icons/fi'
import { validarCPF } from '../../util/validadores'
import cpfApi from '../../services/cpf'
import masks from '../../util/masks'
import { unmask } from '../../util/string'
import { getAge } from '../../util/date'
import Table from '../../components/Table'
import FileStack from '../../components/FileStack'

let uuidAcesso

export default function () {
	const { token, qrcode_token } = useParams()

	const qrcodeValidade = new Date(+qrcode_token)

	const [showFormularioCPF, setshowFormularioCPF] = useState(false)
	const [mostrarCondicoesMedicas, setMostrarCondicoesMedicas] = useState(false)
	const [isTagValid, setIsTagValid] = useState(null)
	const [exameVisualizacao, setExameVisualizacao] = useState(null)
	const [isAllowedAccess, setIsAllowedAccess] = useState(null)

	const { data } = useQuery({
		queryKey: ['get-dados-emergencia-from-token'],
		queryFn: async () => {
			try {
				if(!uuidAcesso) {
					uuidAcesso = uuid()
				}
				
				const response = await api.get(`prontuario/emergencia/dados/${token}`)

				setIsTagValid(!!response)
				
				await api.post('prontuario_acesso', {
					prontuario_id: response.prontuario.id,
					acesso_id: uuidAcesso
				})

				return response || {}
			} catch(e) {
				return {}
			}
		}
	})

	useEffect(() => {
		if(data && qrcodeValidade) {
			const validQrcodeToken = data.prontuario.qrcode_token === qrcode_token
			let isQrcodeTokenExpired = true

			if(validQrcodeToken) {
				isQrcodeTokenExpired = isBefore(qrcodeValidade, new Date())
			}

			setIsAllowedAccess(validQrcodeToken && !isQrcodeTokenExpired)
		}
	}, [qrcodeValidade, data])

	async function handleVerificarCPF({ cpf }) {
		if (!validarCPF(cpf)) {
			toast.warn('O CPF não é válido. Verifique o número digitado.')

			return
		}

		try {
			const { data: response } = await cpfApi.get(unmask(cpf))

			const nome = response.nome

			if (nome) {
				await api.put(`prontuario_acesso/${uuidAcesso}`, {
					cpf,
					nome
				})

				setMostrarCondicoesMedicas(true)

				setshowFormularioCPF(false)
			} else {
				toast.warn('O CPF não está vinculado a nenhuma pessoa. Verifique o número digitado.')
			}

		} catch (e) {
			toast.error(e.msg)
		}
	}

	if (isTagValid === false) {
		return (
			<div className="my-8 md:mx-16 lg:mx-24 flex flex-col items-center">
				<Card className="flex flex-col gap-4 bg-white text-slate-800 max-w-96 items-center">
					<FiAlertTriangle size={40} className="text-red-500" />

					<h1 className="font-bold text-center text-slate-800" style={{ fontSize: 16 }}>
						Este cartão de emergência não foi reconhecido.
					</h1>

					<span className="text-slate-500 self-center" style={{ fontSize: 13 }}>Verifique o QRCode e tente novamente.</span>
					<span className="text-slate-500 self-start" style={{ fontSize: 13 }}>Se o problema persistir, talvez este cartão tenha sido cancelado pelo proprietário.</span>
				</Card>
			</div>
		)
	}

	if (!data || isTagValid === null) {
		return (
			<div className="my-20">
				<Spinner />
			</div>
		)
	}

	const informacoes = data.informacoes.reduce((result, item) => {
		return {
			...result,
			[item.pergunta]: !Number.isNaN(Number(item.resposta)) ? !!Number(item.resposta) : item.resposta
		}
	}, {})

	const dadosClinicos = data.dados_clinicos.reduce((result, item) => {
		return {
			...result,
			[item.tipo]: item.valor
		}
	}, {})

	const exames = data.exames.map(exame => ({
		id: exame.id,
		medico: `${exame.nome} (${exame.crm})`,
		tipo: exame.tipo,
		data_exame: format(new Date(exame.data_exame), 'dd/MM/yyyy'),
		data_resultado: format(new Date(exame.data_resultado), 'dd/MM/yyyy'),
		arquivos_exame: exame.arquivos_exame
	}))

	if(isAllowedAccess === null) {
		return null
	}

	return (
		<div className="my-8 md:mx-16 lg:mx-24 flex flex-col items-center">
			<Card className="flex flex-col gap-6 bg-white text-slate-800 w-full">

				{isAllowedAccess === true ? (
					<>
						<h1 className="font-bold text-center text-slate-800" style={{ fontSize: 18 }}>DADOS MÉDICOS</h1>

						<h1 className="text-lg text-slate-800" style={{ fontWeight: 700 }}>{data.nome}</h1>
						
						{data.data_nascimento && <h2 className="text-slate-800" style={{ fontSize: 15, fontWeight: 500 }}>{getAge(data.data_nascimento)} anos</h2>}

						{!showFormularioCPF && !mostrarCondicoesMedicas && (
							<div className="flex flex-col gap-4">
								<div className="flex flex-col gap-3 mx-2 bg-slate-200 p-2 rounded-md">
									<p>Os dados exibidos nesta tela são dados pessoais e sensíveis pertencentes a <span className="font-bold">{data.nome}</span>.</p>
									<p>O acesso a estas informações só pode ser feito mediante autorização desta pessoa, conforme previsto na <span className="font-bold">Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD)</span>.</p>
									<p>É proibido compartilhar, reproduzir ou utilizar esses dados para qualquer finalidade sem o consentimento do titular. O uso indevido pode acarretar responsabilidades legais.</p>
								</div>

								<Button onClick={() => { setshowFormularioCPF(true) }} className="w-full button">
									Acessar dados médicos de {data.nome}
								</Button>
							</div>
						)}

						{showFormularioCPF && (
							<Formik
								onSubmit={handleVerificarCPF}
								initialValues={{
									cpf: ''
								}}
							>
								{({ isSubmitting }) => (
									<Form className="flex flex-col gap-3">
										<p className="mx-2">Para ter acesso aos dados médicos de <span className="font-bold">{data.nome}</span>, informe seu CPF para verificação.</p>

										<Textbox
											name="cpf"
											label="Seu CPF"
											mask={masks.cpf}
										/>

										<Button type="submit" className="button w-full" loading={isSubmitting}>
											Verificar CPF
										</Button>
									</Form>
								)}
							</Formik>
						)}

						{mostrarCondicoesMedicas && (
							<>
								<div className="flex flex-col gap-2">
									<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Condições médicas</h1>
									<ul className="flex flex-col gap-1 mx-2">
										<li><span className="font-semibold">Pressão alta</span>: {informacoes.pressao_alta ? 'sim' : 'não'}</li>
										<li><span className="font-semibold">Cardíaco</span>: {informacoes.cardiaco ? 'sim' : 'não'}</li>
										<li><span className="font-semibold">Diabético</span>: {informacoes.diabetes ? 'sim' : 'não'}</li>
										<li><span className="font-semibold">Usa marcapasso</span>: {informacoes.marcapasso ? 'sim' : 'não'}</li>
										<li><span className="font-semibold">Usa prótese metálica</span>: {informacoes.protese_metalica ? 'sim' : 'não'}</li>
										<li><span className="font-semibold">Alergias</span>: {informacoes.alergias || 'nenhuma'}</li>
									</ul>
								</div>

								<div className="flex flex-col gap-2">
									<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Dados clínicos</h1>
									<ul className="flex flex-col gap-1 mx-2">
										<li><span className="font-semibold">Tipo sanguíneo</span>: {dadosClinicos.tipo_sangue}</li>
									</ul>
								</div>

								<div className="flex flex-col gap-2">
									<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Doenças preexistentes</h1>

									{data.doencas.length > 0 ? (
										<ul className="flex flex-col gap-1 mx-2">
											{data.doencas.map(doenca => (
												<li key={doenca.id}>
													{doenca.doenca}
												</li>
											))}
										</ul>
									) : <span className="mx-2 italic text-slate-500 font-thin" style={{ fontSize: 13 }}>Não informado</span>}

								</div>

								<div className="flex flex-col gap-2">
									<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Medicamentos de uso contínuo</h1>

									{data.medicamentos.length > 0 ? (
										<ul className="flex flex-col gap-1 mx-2">
											{data.medicamentos.map(medicamento => (
												<li key={medicamento.id}>
													{medicamento.medicamento}
												</li>

											))}
										</ul>
									) : <span className="mx-2 italic text-slate-500 font-thin" style={{ fontSize: 13 }}>Não informado</span>}
								</div>

								<div className="flex flex-col gap-2">
									<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Exames e laudos</h1>

									{!!exames.length ? (
										<>
											{exameVisualizacao && (
												<div className="flex flex-col gap-3 border rounded-md border-slate-300 py-3 px-4 animated fadeInUp faster bg-white">
													<div className="grid grid-cols-[auto_3rem] gap-2 lg:gap-4">
														<div className="w-full flex flex-col lg:flex-row lg:justify-between gap-4 lg:items-center font-semibold">
															<span>{`Solicitante: ${exameVisualizacao.medico}`}</span>

															<div className="flex flex-col lg:flex-row gap-2 lg:gap-6">
																<span>Data do exame: {exameVisualizacao.data_exame}</span>
																<span>Data do resultado: {exameVisualizacao.data_resultado}</span>
															</div>
														</div>

														<FiX size={40} className="hover:bg-slate-200 text-slate-500 p-2 cursor-pointer rounded-md" onClick={() => { setExameVisualizacao(null) }} />
													</div>

													<div>
														<FileStack
															name="arquivos"
															files={exameVisualizacao.arquivos_exame.map((item, idx) => ({
																...item.arquivo,
																name: `Arquivo ${idx + 1}`
															}))}
														/>
													</div>

													<div className="flex justify-center">
														<Button onClick={() => { setExameVisualizacao(null) }} className="">
															Fechar
														</Button>
													</div>
												</div>
											)}

											<Table
												headers={[
													{ name: 'tipo', value: 'Tipo', centered: true },
													{ name: 'medico', value: 'Médico solicitante' },
													{ name: 'data_exame', value: 'Data de realização', centered: true },
													{ name: 'data_resultado', value: 'Data do resultado', centered: true }
												]}
												data={exames}
												filterable
												hideOnSmallHeaders={[
													'data_exame',
													'data_resultado'
												]}
												hideOffset={0}
												hideHeadersOffset={980}
												style={{ marginTop: 0 }}
												actions={[
													{
														name: 'Visualizar',
														action: setExameVisualizacao,
														icon: FiFileText,
														iconSize: 16
													}
												]}
											/>
										</>
									) : (
										<p className="text-slate-500 italic text-sm mx-2">Nenhum exame cadastrado.</p>
									)}
								</div>
							</>
						)}
					</>
				) : (
					<div className="flex flex-col items-center gap-4 italic text-sm text-slate-600 text-center">
						<CircleX size={40} className="text-red-500" />
						Este prontuário não está mais acessível. Solicite a o paciente que gere um novo QRCode de acesso.
					</div>
				)}
			</Card>
		</div>
	)
}
